<script setup>
import VueApexCharts from "vue-apexcharts";
import axios from 'axios';
import { mapState } from "vuex";
import { meanChart, ppkChart, reabilityChart, trendChart } from './utils/ChartOptions';
import { urlMainAPI, defaultSuccess, showError } from "@/global";
import ReasonsToSelect from "./utils/ReasonsToSelect.vue";
import ActionsToSelect from "./utils/ActionsToSelect.vue";
import { faultTreeMixin } from "../mixins/faultTreeMixin";
import OccurrencesButtons from './utils/OccurrencesButtons.vue';
import * as moment from "moment";
</script>
<template>
    <b-overlay class="main" id="overlay-background" :show="loading" rounded="sm">
        <section class="occContainer">
            <section class="graphContainer" v-if="chartOptions">
                <apexcharts v-if="chartOptions.meanChart" :options="chartOptions.meanChart" height="230px"
                    :series="chartOptions.meanChart && chartOptions.meanChart.series != undefined ? chartOptions.meanChart.series : []">
                </apexcharts>
                <apexcharts v-if="chartOptions.ppkChart" :options="chartOptions.ppkChart" height="230px"
                    :series="chartOptions.ppkChart && chartOptions.ppkChart.series != undefined ? chartOptions.ppkChart.series : []">
                </apexcharts>
                <apexcharts v-if="chartOptions.reabilityChart" :options="chartOptions.reabilityChart" height="230px"
                    :series="chartOptions.reabilityChart && chartOptions.reabilityChart.series != undefined ? chartOptions.reabilityChart.series : []">
                </apexcharts>
                <apexcharts v-if="chartOptions.trendChart" :options="chartOptions.trendChart" height="230px"
                    :series="chartOptions.trendChart && chartOptions.trendChart.series != undefined ? chartOptions.trendChart.series : []">
                </apexcharts>
            </section>

            <section class="infosContainer" v-if="occurrence && occurrence.content">
                <div class="occInfoComponent">
                    <p>{{ $t('data-de-abertura') }}</p>
                    <span>{{ formatOccDate(occurrence.creationDate) }}</span>
                </div>

                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsPeriod') }}</p>
                    <span>{{ occurrence.workshiftDescription }}</span>
                </div>
                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsResponsible') }}</p>
                    <span>{{ occurrence.responsibleName }}</span>
                </div>
                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsGoal') }}</p>
                    <span v-if="onlineGoal != null || occurrence.content.target != undefined">{{ onlineGoal == null ?
                        Number(occurrence.content.target.toString().replaceAll(",", ".")).toFixed(2) : onlineGoal
                    }}</span>
                </div>
                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsRealized') }}</p>
                    <span v-if="onlineValue != null || occurrence.content.value != undefined">{{ onlineValue == null ?
                        Number(occurrence.content.value.toString().replaceAll(",", ".")).toFixed(2) : onlineValue
                    }}</span>
                </div>
                <div class="occInfoComponent">
                    <p>LIE</p>
                    <span>{{ this.lowerLimit }}</span>
                </div>
                <div class="occInfoComponent">
                    <p>LSE</p>
                    <span>{{ this.upperLimit }}</span>
                </div>

                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsDeadline') }}</p>
                    <span>{{ formatOccDate(occurrence.deadline, false) }}</span>
                </div>

                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsRelatedIndicator') }}</p>
                    <span>{{
                        occurrence.indicatorName != null
                            ? occurrence.indicatorName
                            : $t("nao-informado")
                    }}</span>
                </div>
                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsProcess') }}</p>
                    <span>{{ occurrence.content.processName ? occurrence.content.processName : $t('nao-informado')
                    }}</span>
                </div>
                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsEquipment') }}</p>
                    <span>{{ occurrence.content.equipmentName && occurrence.content.equipmentName != "" ?
                        occurrence.content.equipmentName : $t('nao-informado') }}</span>
                </div>
                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsProduct') }}</p>
                    <span>{{ occurrence.content.productName && occurrence.content.productName != "" ?
                        occurrence.content.productName : $t('nao-informado') }}</span>
                </div>

                <div class="occInfoComponent">
                    <p>{{ $t('occurrenceDetailsTrigger') }}</p>
                    <span>{{ occurrence.content.triggerName && occurrence.content.triggerName != "" ?
                        occurrence.content.triggerName : "MAN" }}</span>
                </div>

                <template v-if="occurrence.content.triggerName === 'VMH' || occurrence.content.triggerName === 'VMW'">
                    <div class="occInfoComponent vmh-field">
                        <p>{{ $t('media-calculada') }}</p>
                        <span>{{ occurrence.content.calculatedAverage ? occurrence.content.calculatedAverage.toFixed(2)
                            : $t('nao-informado') }}</span>
                    </div>
                    <div class="occInfoComponent vmh-field">
                        <p>{{ $t('limite-minimo') }}</p>
                        <span>{{ occurrence.content.minValue ? occurrence.content.minValue.toFixed(2) :
                            $t('nao-informado') }}</span>
                    </div>
                    <div class="occInfoComponent vmh-field">
                        <p>{{ $t('limite-maximo') }}</p>
                        <span>{{ occurrence.content.maxValue ? occurrence.content.maxValue.toFixed(2) :
                            $t('nao-informado') }}</span>
                    </div>


                    <div class="occInfoComponent vmh-field">
                        <p>{{ $t('menor-valor-durante-o-periodo') }}</p>
                        <span>{{ (occurrence.content.historicData && occurrence.content.historicData.length > 0) ?
                            Math.min(...occurrence.content.historicData).toFixed(2) : $t('nao-informado') }}</span>
                    </div>

                    <div class="occInfoComponent vmh-field">
                        <p>{{ $t('maior-valor-durante-o-periodo') }}</p>
                        <span>{{ (occurrence.content.historicData && occurrence.content.historicData.length > 0) ?
                            Math.max(...occurrence.content.historicData).toFixed(2) : $t('nao-informado') }}</span>
                    </div>
                </template>






                <div class="occInfoComponent" v-if="showSolvace">
                    <p>RCA ID: </p>
                    <span> {{
                        occurrence.content.rcaId != null
                            ? occurrence.content.rcaId
                            : $t('not-found')
                    }}</span>
                </div>
                <div class="occInfoComponent" v-if="showSolvace">
                    <p>RCA Status</p>
                    <span>{{ occurrence.content.rcaStatus && occurrence.content.rcaStatus == 10 ? $t('open') :
                        occurrence.content.rcaStatus ? $t('closed') : $t('not-found') }}</span>
                </div>
                <div class="occInfoComponent" v-if="showWorkerOrder == true && occurrence.content.workOrder != null">
                    <p>{{ $t('ordem-de-servico') }}:</p>
                    <section class="workerOrderComponent">
                        <input type="text" v-model="occurrence.content.workOrder" v-if="editingWorkerOrder">
                        <span v-else>{{ occurrence.content.workOrder }}</span>
                        <b-icon-pencil-square v-if="canHandleOccurrence == true && !editingWorkerOrder"
                            @click="() => this.editingWorkerOrder = true" />
                        <b-icon-check-circle v-if="canHandleOccurrence == true && editingWorkerOrder"
                            @click="changeWorkerOrder" />
                    </section>
                </div>
            </section>

            <section class="handledbycontainer"
                v-if="this.occurrence && this.occurrence.statusId != 1 && this.occurrence.statusId != 3">
                <strong>{{ this.$t('handled-by') }}</strong>
                <span>{{ this.handledByName != '' ? this.handledByName : occurrence.responsibleName }}</span>
            </section>

            <section class="handledbycontainer" v-if="this.occurrence && this.occurrence.statusId == 3 && this.occurrence">
                <strong>{{ this.$t('canceled-by') }}</strong>
                <span>{{ 
                    this.occurrence.reasonContent && this.occurrence.reasonContent.userThatCancel ? 
                    this.occurrence.reasonContent.userThatCancel.name : 
                    (this.occurrence.reasonContent && this.occurrence.reasonContent.canceledBy ? 
                    this.occurrence.reasonContent.canceledBy.name : 
                    occurrence.responsibleName) 
                }}</span>
            </section>

            <section class="handledbycontainer" v-if="this.occurrence && this.occurrence.statusId == 3 && this.occurrence && this.occurrence.description && this.occurrence.description.includes(' | Cancelada:')">
                <strong>Justificativa do cancelamento:</strong>
                <span>{{ this.occurrence.description.split(' | Cancelada:')[1].trim() }}</span>
            </section>

            <section class="reasonTitle" v-if="this.occurrence && this.occurrence.statusId != 3">
                <p>{{ $t('reason-and-action') }}</p>
                <button v-if="canHandleOccurrence == true" @click="openReasonModal()">
                    <span>{{ $t('add-reason') }}</span>
                    <b-icon-plus-circle-fill></b-icon-plus-circle-fill>
                </button>
            </section>
            <section v-if="this.reasonList.length > 0" class="reasonActionContainer">
                <details v-for="(reason, idx) in this.reasonList" :key="`reason-${idx}-${Math.random()}`">
                    <summary>
                        <span :title="reason.title">{{ reason.title }}</span>
                        <button v-if="user && reason.createdBy && user.Id.toString() == reason.createdBy.toString()"
                            @click="openRemoveModal('deleteReasonModal', idx, null)" class="removeReasonBtn"
                            :title="$t('remove-reason')">
                            <!-- <span>{{ $t('remove-reason') }}</span> -->
                            <b-icon-trash-fill />
                        </button>
                    </summary>
                    <article>
                        <section class="actionsContainer" v-if="reason.actions && reason.actions.length > 0">
                            <section v-for="(action, acIdx) in reason.actions"
                                :key="`actions-${acIdx}-${Math.random()}`">
                                <section class="actionItem">
                                    <div class="actionTitle">
                                        <strong> {{ $t('actionTableActionHeader') }}:</strong>
                                        <span :title="action.action">{{ action.action }}</span>
                                    </div>
                                    <div>
                                        <strong>{{ $t('actionTableResponsibleHeader') }}:</strong>
                                        <span :title="action.responsableName">{{ action.responsableName }}</span>
                                    </div>
                                    <div
                                        v-if="action.status == null || !action.status.toLowerCase().includes('concluída')">
                                        <strong>{{ $t('actionTableDeadlineHeader') }}:</strong>
                                        <span>{{ formatOccDate(action.date) }}</span>
                                    </div>
                                    <div v-else>
                                        <strong>{{ $t('conclusion') }}:</strong>
                                        <span>{{ formatOccDate(action.closingDate).split('-')[0].trim() }}</span>
                                    </div>
                                    <div>
                                        <div
                                            v-if="action.status != null && action.status.toLowerCase().includes('concluída')">
                                            <strong>{{ $t('status-0') }}:</strong>
                                            <span>{{ action.status }}</span>
                                        </div>
                                        <div v-else class="actionBtn">
                                            <b-icon-trash-fill
                                                v-if="user && action.createdBy && (isAux || user.Id.toString() == occurrence.responsibleId.toString() || action.createdBy && user.Id.toString() == action.createdBy.toString())"
                                                @click="openRemoveModal('deleteActionModal', idx, acIdx)"
                                                title="Remover Ação"></b-icon-trash-fill>
                                            <b-icon-pencil-fill
                                                v-if="user && (isAux || user.Id.toString() == occurrence.responsibleId.toString() || action.createdBy && user.Id.toString() == action.createdBy.toString())"
                                                title="Editar Ação"
                                                @click="openActionModal(reason, action)"></b-icon-pencil-fill>
                                            <b-icon-check-circle-fill
                                                v-if="user && (user.Id.toString() == action.responsableId.toString() || (canDealReasonAction != undefined && canDealReasonAction == true))"
                                                @click="openConfirmAction(idx, acIdx)"
                                                title="Concluir Ação"></b-icon-check-circle-fill>
                                        </div>
                                    </div>
                                </section>
                            </section>
                        </section>
                        <p class="voidActions" v-else>{{ $t('no-action-on-reason') }}</p>
                        <button v-if="canHandleOccurrence == true" class="addActionBtn"
                            @click="openActionModal(reason, null)">
                            <span>{{ $t('addActionButtom') }}</span>
                            <b-icon-plus-circle-fill></b-icon-plus-circle-fill>
                        </button>
                    </article>
                </details>

            </section>
            <p v-else>{{ $t('no-reason-found') }}</p>

            <section class="obsContainer">
                <div>
                    <span>{{ $t('occurrenceDetailsNotes') }}</span>
                    <textarea id="occObservation" cols="70" rows="2" v-if="occurrence"
                        v-model="occurrence.observation"></textarea>
                </div>
                <div>
                    <span>{{ $t('occurrenceDetailsLessonsLearned') }}</span>
                    <textarea id="occLessionLearned" cols="70" rows="2" v-if="occurrence && occurrence.content"
                        v-model="occurrence.content.lessonsLearned"></textarea>
                </div>
            </section>
        </section>

        <section class="mt-3" v-if="!this.occurrenceInfo">
            <div class="row">
                <div class="col-12">
                    <div class="div-table">
                        <table class="table table-hover table-striped table-sm">
                            <thead class="thead-dark header">
                                <th hidden scope="col">{{ $t("id") }}</th>
                                <th scope="col">{{ $t("gatilho") }}</th>
                                <th class="text-center" scope="col">{{ $t("data") }}</th>
                                <th class="text-center" scope="col">{{ $t("periodo-0") }}</th>
                                <th class="text-center" scope="col">{{ $t("responsavel") }}</th>
                                <th class="text-center" scope="col">{{ $t("prazo") }}</th>
                                <th class="text-center" scope="col">{{ $t("status-0") }}</th>
                            </thead>
                            <tbody>
                                <tr v-for="(occurrenceFromList, i) in this.otherOccurrences"
                                    :class="`occurrence-${occurrenceFromList.id}`"
                                    :key="`${occurrenceFromList.id}-${i}`" @dblclick="selectOccurrence">
                                    <td hidden>{{ occurrenceFromList.id }}</td>
                                    <td>{{ occurrenceFromList.trigger ? occurrenceFromList.trigger :
                                        occurrenceFromList.content.triggerName }}</td>
                                    <td class="text-center">
                                        {{ formatOccDate(occurrenceFromList.creationDate) }}
                                    </td>
                                    <td class="text-center">
                                        {{ occurrenceFromList.workshiftDescription }}
                                    </td>
                                    <td class="text-center">{{ occurrenceFromList.responsibleName }}</td>
                                    <td class="text-center">{{ formatOccDate(occurrenceFromList.deadline) }}</td>
                                    <td class="text-center">{{ occurrenceFromList.statusDescription }}</td>
                                </tr>
                                <tr v-if="this.otherOccurrences.length == 0">
                                    <td colspan="6">{{ $t("no-occurrences-show") }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </section>
        <hr>
        <OccurrencesButtons :canSaveOccurrence="canSaveOccurrence" :canCancelOccurrence="canCancelOccurrence"
            :messageStatus="messageToFinishOcc" :saveOccurrenceFunction="saveOccurrence"
            :cancelOccurrenceFunction="openCancelOccurenceModal" :closeOccurrenceFunction="closeOccurrenceModal"
            :canFinishOccurrence="canFinishOccurrence" />

        <b-modal size="lg" hide-footer :title='$t("selectReasonModal")' id="selectReasonTree">
            <ReasonsToSelect :reasonTree="reasonTree" :addReasonFunction="addReasonToList" :userObj="user">
            </ReasonsToSelect>
        </b-modal>

        <b-modal size="lg" hide-footer :title='$t("selecione-uma-acao")' id="selectActionTree">
            <ActionsToSelect :indicatorId="indicator ? indicator.id : null" :userObj="user"
                :reasonTreeId="currentReasonTreeId" :reasonId="currentReasonId" :addActionFunction="addActionToList"
                :responsiblesList="responsibleList" :actionObj="editingAction"
                :showActionResponsible="showActionResponsible"
                :occurrenceResponsible="occurrence && occurrence.responsibleId ? occurrence.responsibleId : null"
                :needDeadline="needDeadline"></ActionsToSelect>
        </b-modal>

        <b-modal id="deleteReasonModal" title="Remover Motivo" ok-variant=danger :ok-title="$t('yes')"
            :cancel-title="$t('no')" @ok="removeReason">
            <p>{{ $t('confirm-remove-reason') }}</p>
        </b-modal>

        <b-modal id="deleteActionModal" title="Remover Ação" ok-variant=danger :ok-title="$t('yes')"
            :cancel-title="$t('no')" @ok="removeAction">
            <p>{{ $t('confirm-remove-action') }}</p>
        </b-modal>

        <b-modal id="confirmActionModal" title="Concluir Ação" ok-variant=danger :ok-title="$t('yes')"
            :cancel-title="$t('no')" @ok="confirmAction">
            <p>{{ $t('confirm-finish-action') }}</p>
        </b-modal>

        <b-modal id="cancelOccModal" title="Cancelar Ocorrência" ok-variant=danger :ok-title="$t('yes')"
            :cancel-title="$t('no')" @ok="cancelOccurrence">
            <p>Deseja realmente <strong>cancelar</strong> está ocorrência?</p>
            <p>Escreva abaixo o motivo do cancelamento:</p>
            <textarea id="" rows="3" v-model="finishOrCancelUserMessage"></textarea>
        </b-modal>

    </b-overlay>
</template>
<script>
export default {
    name: "ManageAllOccurrences",

    props: [
        "indicatorInfo",
        "chartsInfo",
        "occurrenceInfo",
        "reasonActionInfo",
        "otherOccurrencesInfo",
        "reloadOccFunct",
        "showActionResponsible",
        "viewMode"
    ],

    components: { apexcharts: VueApexCharts, ReasonsToSelect, ActionsToSelect, OccurrencesButtons },

    mixins: [faultTreeMixin],

    computed: {
        ...mapState(["user"])
    },

    data() {
        return {
            loading: false,
            indicator: {},
            occurrence: {},
            reasonList: [],
            otherOccurrences: [],
            chartOptions: {},
            trendStartDate: moment(),
            trendEndDate: moment(),
            showWorkerOrder: false,
            showSolvace: false,
            lessonLearnedInput: "",
            observationInput: "",
            currentReasonId: null,
            currentReasonTreeId: null,
            reasonTree: {},
            responsibleList: [],
            onlineGoal: null,
            onlineValue: null,
            needDeadline: false,
            messageToFinishOcc: '',
            formattedCurrentValue: 0,
            editingAction: {},
            deletingReasonIdx: null,
            deletingActionIdx: null,
            confirmingActionIdx: null,
            confirmingReasonIdx: null,
            editingWorkerOrder: false,
            checkFinishStatus: 0,
            finishOrCancelUserMessage: '',

            userCanDealOcc: false,

            canSaveOccurrence: false,
            canFinishOccurrence: false,
            canHandleOccurrence: false,
            canCancelOccurrence: false,
            handledByName: "",

            lowerLimit: "-",
            upperLimit: "-",
            canDealReasonAction: false,
            isAux: false
        }
    },

    watch: {
        "checkFinishStatus"() {
            this.checkCanFinish()
        }
    },

    methods: {
        checkUserPermissions() {
            if (this.viewMode && this.viewMode == true) return

            this.isAux = false;

            if (typeof this.occurrence.content == "string") this.occurrence.content = JSON.parse(this.occurrence.content);
            if (this.occurrence && this.occurrence.statusId != 1) {
                this.canHandleOccurrence = false;
                this.canSaveOccurrence = false;
                this.canFinishOccurrence = false;
                this.canCancelOccurrence = false;
                this.canDealReasonAction = false;
                this.messageToFinishOcc = 'Não é possível alterar ocorrências não abertas.';
                this.userCanDealOcc = false;
                return;
            } else {
                if (this.occurrence.reasonContent != null && typeof this.occurrence.reasonContent == "string") this.occurrence.reasonContent = JSON.parse(this.occurrence.reasonContent)
                let hasMainResp = this.occurrence.reasonContent != null && this.occurrence.reasonContent.mainResponsibleId != undefined && this.occurrence.reasonContent.mainResponsibleId.id != undefined;
                let hasAuxResp = this.occurrence.reasonContent != null && this.occurrence.reasonContent.auxResponsiblesId != undefined && this.occurrence.reasonContent.auxResponsiblesId.length && this.occurrence.reasonContent.auxResponsiblesId.length > 0;
                let userOnAuxList = hasAuxResp && this.occurrence.reasonContent.auxResponsiblesId.filter(resp => resp.id.toString() == this.user.Id.toString())
                let userOnActionResp = this.occurrence.content.actionsList && this.occurrence.content.actionsList.length > 0 && this.occurrence.content.actionsList.filter(x => this.user.Id.toString() == x.responsableId.toString()).length > 0


                this.isAux = userOnAuxList.length > 0;

                // Verifica se o usuário é um responsável (principal ou auxiliar)
                let isResponsible = this.user.userRoleDescription.toString().toLowerCase().includes('admin') ||
                    this.user.Id.toString() == this.occurrence.responsibleId.toString() ||
                    (hasMainResp && this.occurrence.reasonContent.mainResponsibleId.id.toString() == this.user.Id.toString()) ||
                    (hasAuxResp && userOnAuxList.length > 0);

                // Só permite cancelar se tiver a permissão E for um responsável
                this.canCancelOccurrence = (this.user.Permissions.cancelarocorrencia != undefined &&
                    this.user.Permissions.cancelarocorrencia.allowed == true) && isResponsible;

                if (isResponsible) {
                    if (this.user.Permissions.fecharocorrencias != undefined && this.user.Permissions.fecharocorrencias.allowed == true) {
                        if (this.user.canHandleOccurrences != undefined && this.user.canHandleOccurrences == "true") {
                            this.canFinishOccurrence = true;
                            this.canSaveOccurrence = true;
                            this.canHandleOccurrence = true;
                            this.userCanDealOcc = true;
                            this.canDealReasonAction = true;

                            return;
                        } else {
                            this.messageToFinishOcc = 'Seu usuário não tem permissão para tratar ocorrências.';
                        }
                    } else {
                        this.messageToFinishOcc = 'Seu usuário não tem permissão para fechar as ocorrências.';
                    }
                }

                this.canFinishOccurrence = false;
                this.canSaveOccurrence = false;
                this.canHandleOccurrence = false;
                this.userCanDealOcc = false;
                this.canDealReasonAction = false;

                if (userOnActionResp == true) {
                    this.canDealReasonAction = true;
                }
            }
        },

        checkCanFinish() {
            if (this.userCanDealOcc == true || this.canDealReasonAction == true) {
                this.canSaveOccurrence = true;
                this.canFinishOccurrence = true;

                if (this.reasonList.length == 0) {
                    this.canFinishOccurrence = false;
                    this.canSaveOccurrence = false;
                    this.messageToFinishOcc = 'Não existem motivos e ações cadastradas.';
                    return;
                }

                this.reasonList.forEach(item => {
                    if (!item.actions || item.actions.length == 0) {
                        this.canFinishOccurrence = false;
                        this.canSaveOccurrence = false;
                        this.messageToFinishOcc = 'Existem motivos sem ações cadastradas.';
                        return;
                    } else if (item.actions && item.actions.length > 0) {
                        let pendingActions = item.actions.find(act => {
                            if (act.status == null) {
                                let dateParts = act.date.split("/");
                                let day = parseInt(dateParts[0], 10);
                                let month = parseInt(dateParts[1], 10) - 1;
                                let year = parseInt(dateParts[2], 10);

                                let deadlineDate = new Date(year, month, day);
                                deadlineDate.setHours(0, 0, 0, 0);

                                let today = new Date();
                                today.setHours(0, 0, 0, 0);

                                if (deadlineDate >= today) {
                                    return false;
                                }

                                return true;
                            }

                            return false;
                        });

                        if (pendingActions != null && pendingActions != undefined) {

                            this.canSaveOccurrence = false;
                            this.canFinishOccurrence = false;
                            this.messageToFinishOcc = 'Existem ações pendentes que devem ser finalizadas.';
                            return;
                        }
                    }


                    if (this.needDeadline) {
                        this.canFinishOccurrence = true;
                    }

                    this.messageToFinishOcc = "";
                });
            }
        },

        async changeWorkerOrder() {
            this.editingWorkerOrder = false;
            this.loading = true;

            let newWorkerOrder = this.occurrence.content.workOrder

            await axios.post(`${urlMainAPI}/saveworkorder/${this.occurrence.id}`, JSON.stringify(newWorkerOrder), {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then((res) => {
                    defaultSuccess(res.data);
                }).catch(err => console.error(err))

            this.loading = false;
        },

        async loadChartsData(user, occurenceProp, indicator) {
            let sendObj = {};
            sendObj.key = `KP0${user.UnitId}000${indicator.id}`;
            sendObj.initialTimestamp = occurenceProp.creationDate != null ? occurenceProp.creationDate : occurenceProp.baseDate;
            sendObj.agregation = "ON";

            await axios.post(`${urlMainAPI}/getdatauntreatedoccurrence`, sendObj).then(res => {
                this.chartOptions.trendChart = res.data.calcOcChart.trend;
                this.chartOptions.meanChart = res.data.calcOcChart.meanChart;
                this.chartOptions.ppkChart = res.data.calcOcChart.ppkChart;
                this.chartOptions.reabilityChart = res.data.calcOcChart.reabilityChart;

                const initialTimestamp = new Date(res.data.initialTimestamp);
                const finalTimestamp = new Date(res.data.finalTimestamp);

                const dateRange = this.generateDateRange(initialTimestamp, finalTimestamp);

                this.chartOptions.trendChart.series.forEach(item => {
                    let dataArray = item.data;
                    let newSeriesData = this.fillMissingDates(dateRange, dataArray);
                    item.data = [...newSeriesData]
                });

                this.chartOptions.trendChart.xaxis = {
                    type: "datetime",
                    min: initialTimestamp.getTime(),
                    max: finalTimestamp.getTime(),
                    tickAmount: 6,
                    tooltip: {
                        enabled: false,
                    },
                    labels: {
                        datetimeUTC: false,
                    }
                }


                if (this.indicator.indicatorRule && this.indicator.indicatorRule.trim() != "" && this.indicator.indicatorRule.trim() != "-" &&
                    this.occurrence.content.target && this.occurrence.content.target != null && this.occurrence.content.target != "" && this.occurrence.content.target != -9999) {
                    let criticalRange = indicator.indicatorRule.split(" ")[2];

                    if (!Number.isNaN(criticalRange.replace(",", "."))) {
                        let percentegeValue = (Number(this.occurrence.content.target) * Number(criticalRange.replace(",", "."))) / 100;

                        this.lowerLimit = (Math.floor((Number(this.occurrence.content.target) - percentegeValue) * 100) / 100).toFixed(2);
                        this.upperLimit = (Math.floor((Number(this.occurrence.content.target) + percentegeValue) * 100) / 100).toFixed(2);

                        if (indicator.indicatorRule.split(" ")[0].trim() == ">") this.lowerLimit = "-"
                        else if (indicator.indicatorRule.split(" ")[0].trim() == "<") this.upperLimit = "-"
                    }
                }

                if (res.data.calcType == 'online') {
                    this.onlineGoal = `${res.data.onlineExtraInfo.adherenceGoal}%`;
                    this.onlineValue = `${res.data.onlineExtraInfo.adherenceValue}`;
                } else {
                    this.onlineGoal = null;
                    this.onlineValue = null;
                }

            }).catch(err => {
                console.log("ERR: ", err);
            })
            this.loading = false;
        },

        generateDateRange(startDate, endDate) {
            const dates = [];
            let currentDate = new Date(startDate);
            const end = new Date(endDate);

            while (currentDate <= end) {
                dates.push(new Date(currentDate));
                currentDate.setHours(currentDate.getHours() + 1);
            }
            return dates;
        },

        fillMissingDates(dateRange, data) {
            if (!Array.isArray(data) || data.length == 0) return data

            let sortedData = [...data].sort((a, b) => new Date(a.x) - new Date(b.x));

            let returnedData = [...sortedData];
            for (let item of dateRange) {
                const isoDate = item.toISOString().slice(0, 19).replace('T', ' ');
                if (new Date(isoDate) < new Date(returnedData[0].x)) {
                    returnedData.unshift({
                        x: isoDate,
                        y: null,
                        z: null
                    });
                }
                if (new Date(isoDate) > new Date(returnedData[returnedData.length - 1].x)) {
                    returnedData.push({
                        x: isoDate,
                        y: null,
                        z: null
                    });
                }
            }

            return returnedData;
        },

        async loadUntreatedOccurrences() {
            const url = `${urlMainAPI}/getuntreatedoccurrences/${this.indicator.id}`;
            await axios.get(url)
                .then((res) => {
                    if (res.data.length > 0) {
                        this.otherOccurrences = res.data;


                        this.otherOccurrences.forEach(element => {
                            element.content = JSON.parse(element.content);
                            element.content = element.content != null ? element.content : '';
                            if (element.reasonContent != null) element.reasonContent = JSON.parse(element.reasonContent);
                        });

                        this.occurrence = this.otherOccurrences[0];
                    }
                }).catch((e) => {
                    console.error(e);
                });
        },

        async loadOcurrenceById() {
            const url = `${urlMainAPI}/getuntreatedoccurrencebyid/${this.occurrence.id}`;
            await axios.get(url)
                .then((res) => {
                    if (res.data.length > 0) {
                        this.occurrence = res.data[0];
                    }
                }).catch((e) => {
                    console.error(e);
                });
        },

        buildReasonList(occurrenceToBuild) {
            if (!occurrenceToBuild.reasonContent || occurrenceToBuild.reasonContent == null || occurrenceToBuild.reasonContent == '' || !occurrenceToBuild.reasonContent.reasons || !occurrenceToBuild.reasonContent.reasons.length || occurrenceToBuild.reasonContent.reasons.length == 0) {
                let reasonsSplitted = [];
                if (occurrenceToBuild.description.includes(";")) {
                    reasonsSplitted = occurrenceToBuild.description.split(";");
                } else {
                    reasonsSplitted = [occurrenceToBuild.description];
                }

                if (occurrenceToBuild.content && typeof occurrenceToBuild.content == "string") occurrenceToBuild.content = JSON.parse(occurrenceToBuild.content)

                if (occurrenceToBuild.content && occurrenceToBuild.content.actionsList) {
                    occurrenceToBuild.content.actionsList.forEach((item) => {
                        item.reasonId = 0;
                        item.actionId = Math.random();
                        if (!item.responsableName || item.responsableName.toLowerCase().includes("encontrado")) item.responsableName = this.findResponsableById(item.responsableId)
                    })
                }


                reasonsSplitted.forEach((item, idx) => {
                    if (item != '' && item.includes(">")) {
                        let reasonTitle = item.includes(">") ? item : item.toString().substring(0, item.indexOf(". "))


                        this.reasonList.push({
                            id: idx,
                            title: reasonTitle,
                            createdBy: '',
                            actions: occurrenceToBuild.content && occurrenceToBuild.content.actionsList ? occurrenceToBuild.content.actionsList.filter(action => action.reasonId == idx) : [],
                            hasOtherDesc: false,
                            reasonOtherDescription: '',
                            reasonTreeId: this.findReasonTreeId(reasonTitle),
                        })
                    }

                });
            } else {

                if (occurrenceToBuild.reasonContent && typeof occurrenceToBuild.reasonContent == "string") occurrenceToBuild.reasonContent = JSON.parse(occurrenceToBuild.reasonContent)

                if (occurrenceToBuild.reasonContent.reasons) {
                    occurrenceToBuild.reasonContent.reasons.forEach(item => {
                        if (item.actions) {
                            item.actions.forEach(acc => acc.responsableName = this.findResponsableById(acc.responsableId))
                        }

                    })

                    this.reasonList = occurrenceToBuild.reasonContent.reasons;
                }

            }

            this.checkFinishStatus++;
        },

        async loadResponsibles(unitIdProps) {
            const unitId = unitIdProps || 1;

            const url = `${urlMainAPI}/getresponsiblesbyunit/${unitId}`;
            axios.get(url).then((res) => {
                this.responsibleList = res.data.responsibleList;
            });
        },

        async selectOccurrence(event) {
            const tr = event.target.parentElement;
            let occurrenceId = tr.className.split("-")[1].replace(/\D/g, "");

            if (this.occurrence.id.toString() == occurrenceId.toString() || occurrenceId.toString().includes(this.occurrence.id.toString())) {
                defaultSuccess(this.$t('ocorrencia-selecionada'));
                return;
            }

            this.loading = true;

            let previousTr = document.querySelector(".occurrence-selected");
            if (previousTr) previousTr.classList.remove("occurrence-selected");

            tr.classList.add("occurrence-selected");
            this.occurrenceId = isNaN(Number(occurrenceId)) ? this.occurrenceId : Number(occurrenceId);

            let occurrenceSelected = this.otherOccurrences.find(x => x.id == occurrenceId)

            if (!occurrenceSelected) {
                this.loading = false;
                return;
            }

            this.occurrence = { ...occurrenceSelected };

            await this.buildOccurrenceScreen();

            this.loading = false;
        },

        async getFaultTreeByIndicatorId(indicatorId, userObj) {
            await axios
                .get(`${urlMainAPI}/getfaulttree/${indicatorId}/${userObj.UnitId}/${userObj.onlineType[0]}`)
                .then((res) => {
                    if (res.data) {
                        this.reasonTree = this.copyFrom(res.data);

                        if (this.reasonTree.state && this.reasonTree.state.expanded) this.reasonTree.state.expanded = true;
                    }
                })
                .catch(err => console.error(err));
        },

        async getSystemConfigurations() {
            const url = `${urlMainAPI}/getsystemconfiguration`;
            await axios.get(url).then((res) => {
                this.showWorkerOrder = res.data.workOrderInOccurrence
            }).catch(err => console.error(err));
        },

        findReasonTreeId(reasonText) {
            if (reasonText.includes(">")) {
                let reasonSplitted = reasonText.split(">");
                if (reasonSplitted.length > 0) {
                    let reasonToFound = reasonSplitted[reasonSplitted.length - 1].trim();

                    if (reasonToFound.includes(":")) {
                        let reasonArr = reasonToFound.split(":");
                        reasonToFound = reasonArr[0];
                    }

                    let foundReasonOnTree = this.findOnTree([this.reasonTree], 'text', reasonToFound.trim());

                    if (foundReasonOnTree == null) {
                        reasonToFound = reasonSplitted[reasonSplitted.length - 2].trim()
                        foundReasonOnTree = this.findOnTree([this.reasonTree], 'text', reasonToFound.trim());
                        if (foundReasonOnTree != null) return foundReasonOnTree.id;
                    } else {
                        return foundReasonOnTree.id;
                    }
                }
            }

            return null;
        },

        findResponsableById(id) {
            let responsibleFound = this.responsibleList.find(x => x.id.toString() == id.toString());
            if (responsibleFound != null) return responsibleFound.name;
            else return 'Responsável não encontrado.'
        },

        formatOccDate(dateStr, withoutHour) {
            if (dateStr == null || dateStr == undefined || dateStr.includes("/")) return dateStr

            let dtObj = new Date(dateStr);

            let dateFormat = ("0" + dtObj.getDate()).slice(-2);
            let monthFormat = ("0" + (dtObj.getMonth() + 1)).slice(-2);

            let formattedString = `${dtObj.getFullYear()}/${monthFormat}/${dateFormat}`;

            if (dateStr.includes(":") && (withoutHour == undefined || withoutHour == null || withoutHour == true)) {
                let hourFormat = ("0" + dtObj.getHours()).slice(-2);
                let minuteFormat = ("0" + dtObj.getMinutes()).slice(-2);
                let secondFormat = ("0" + dtObj.getSeconds()).slice(-2);
                formattedString += ` - ${hourFormat}:${minuteFormat}:${secondFormat}`;
            }

            return formattedString;
        },

        openReasonModal() {
            this.$bvModal.show("selectReasonTree")
        },

        openActionModal(reasonObj, editingObj) {
            if (editingObj != null) {
                this.editingAction = editingObj;
            }

            this.currentReasonId = reasonObj.id;
            this.currentReasonTreeId = reasonObj.reasonTreeId;
            this.$bvModal.show("selectActionTree");
        },

        addReasonToList(reasonObj) {
            let reasonAlreadyAdded = this.reasonList.find(item => item.title == reasonObj.title);

            if (reasonAlreadyAdded != null) {
                this.$bvToast.toast('Motivo duplicado não adicionado!', {
                    title: 'O motivo selecionado já existe na lista.',
                    variant: 'warning',
                    toaster: 'b-toaster-bottom-left',
                    solid: true
                });

                return;
            }
            this.reasonList.push(reasonObj);
            this.checkFinishStatus++;
        },

        addActionToList(actionToAdd, reasonId) {
            this.reasonList.forEach(item => {
                if (item.id == reasonId) {
                    let actionFound = item.actions.findIndex(ac => ac.id == actionToAdd.actionId || ac.actionId == actionToAdd.actionId);

                    if (actionFound == -1) {
                        item.actions.push(actionToAdd)
                    } else {
                        item.actions.splice(actionFound, 1, actionToAdd)
                    }
                }
            });

            this.checkFinishStatus++;

            this.editingAction = {};
        },

        removeReason() {
            if (this.deletingReasonIdx != null) {
                this.reasonList.splice(this.deletingReasonIdx, 1);
            }

            this.deletingReasonIdx = null;
            this.deletingActionIdx = null;

            this.checkFinishStatus++;
        },

        removeAction() {
            if (this.deletingReasonIdx != null && this.deletingActionIdx != null) {
                this.reasonList[this.deletingReasonIdx].actions.splice(this.deletingActionIdx, 1);
            }

            this.deletingReasonIdx = null;
            this.deletingActionIdx = null;

            this.checkFinishStatus++;
        },

        confirmAction() {
            if (this.confirmingReasonIdx != null && this.confirmingActionIdx != null) {
                this.$set(this.reasonList[this.confirmingReasonIdx].actions[this.confirmingActionIdx], 'status', "Concluída");
                let closingDate = new Date().toLocaleDateString("pt-BR");
                this.$set(this.reasonList[this.confirmingReasonIdx].actions[this.confirmingActionIdx], 'closingDate', closingDate);
            }

            this.confirmingReasonIdx = null;
            this.confirmingActionIdx = null;

            this.checkFinishStatus++;
            this.$forceUpdate();

        },

        openRemoveModal(modalText, reasonIdx, actionIdx) {
            this.deletingReasonIdx = reasonIdx;
            this.deletingActionIdx = actionIdx;
            this.$bvModal.show(modalText);
        },

        openConfirmAction(reasonIdx, actionIdx) {
            this.confirmingReasonIdx = reasonIdx;
            this.confirmingActionIdx = actionIdx;
            this.$bvModal.show("confirmActionModal")
        },

        async getIndicatorById(indicatorId) {
            if (indicatorId == null) return;
            await axios.get(`${urlMainAPI}/getindicatorbyid/${indicatorId}`).then(res => {
                this.indicator = res.data;
            }).catch(err => console.error(err))
        },

        async buildOccurrenceScreen() {

            this.reasonList = [];
            if (this.occurrence == undefined || this.occurrence == null || !this.occurrence.id) {
                await this.loadUntreatedOccurrences();
            }

            if (typeof this.occurrence.content == "string") this.occurrence.content = JSON.parse(this.occurrence.content);
            if (this.occurrence.reasonContent && typeof this.occurrence.reasonContent == "string" && this.occurrence.reasonContent != "") this.occurrence.reasonContent = JSON.parse(this.occurrence.reasonContent)

            this.checkUserPermissions();
            this.buildReasonList(this.occurrence);

            this.lessonLearnedInput = this.occurrence.content.lessonsLearned != null ? this.occurrence.content.lessonsLearned : '';
            this.observationInput = this.occurrence.observation != null ? this.occurrence.observation : '';


            await this.loadChartsData(this.user, this.occurrence, this.indicator);

            this.formattedCurrentValue = "Não encontrado";

            if (this.indicator.onlineValue && this.indicator.onlineValue != "-9999" && this.indicator.onlineValue != null) this.formattedCurrentValue = Number(this.indicator.onlineValue.toString().replaceAll(",", ".")).toFixed(2)
            else if (this.occurrence.content.currentValue != null && this.occurrence.content.currentValue != "-9999") this.formattedCurrentValue = Number(this.occurrence.content.currentValue.toString().replaceAll(",", ".")).toFixed(2)

            if (this.occurrence.statusId != 1) this.canSaveOccurrence = false;

            this.checkFinishStatus++;
        },

        closeOccurrenceModal() {
            this.$bvModal.hide('UntratedOccurrences')
        },

        openCancelOccurenceModal() {
            this.$bvModal.show('cancelOccModal');
        },

        async cancelOccurrence() {
            this.loading = true;
            await this.saveOccurrence(1);
            this.loading = false;
        },

        openFinishOccurenceModal() {
            this.$bvModal.show('finishOccModal');
        },

        async saveOccurrence(finishOcc) {
            this.loading = true;

            if (typeof this.occurrence.content == "string") this.occurrence.content = JSON.parse(this.occurrence.content);

            let occurrenceToSave = { ...this.occurrence }

            let newDescription = "";
            let newActionArr = [];

            this.reasonList.forEach(item => {
                item.actions.forEach(ac => delete ac.responsableName)
                newActionArr.push(...item.actions);
                newDescription += `${item.title};`
            });

            occurrenceToSave.content.actionsList = newActionArr;
            occurrenceToSave.description = newDescription;

            const allActionsCompleted = occurrenceToSave.content.actionsList.every(action => action.status === "Concluída");


            let userHasPermission = false;

            if (this.user.Permissions && (this.user.Permissions.fecharocorrencias || this.user.Permissions.fecharocorrenciageral)) {
                if (this.user.Permissions.fecharocorrencias && this.user.Permissions.fecharocorrencias.allowed == true) userHasPermission = true;
                if (this.user.Permissions.fecharocorrenciageral && this.user.Permissions.fecharocorrenciageral.allowed == true) userHasPermission = true;
            }

            if (finishOcc == 0 && this.canFinishOccurrence == true && userHasPermission == true) {
                if (allActionsCompleted) {
                    occurrenceToSave.content.consecutiveObservations = "-9999";
                    occurrenceToSave.content.requestClosing = true;
                    occurrenceToSave.statusId = 2;
                    occurrenceToSave.finishedDate = moment().format("YYYY-MM-DD HH:mm:ss");
                    occurrenceToSave.description += ` | Encerrada`;
                    occurrenceToSave.responsibleId = this.user.Id;
                    occurrenceToSave.responsibleName = this.user.FullName;
                    occurrenceToSave.responsibleUnitId = this.user.UnitId;
                } else {
                    occurrenceToSave.description += ` | Aguardando prazo`;
                }
            } else if (finishOcc == 1) {
                this.handledByName = this.user.FullName;
                occurrenceToSave.statusId = 3;
                occurrenceToSave.finishedDate = moment().format("YYYY-MM-DD HH:mm:ss");
                occurrenceToSave.description += ` | Cancelada: ${this.finishOrCancelUserMessage}`
                occurrenceToSave.responsibleId = this.user.Id;
                occurrenceToSave.responsibleName = this.user.FullName;
                occurrenceToSave.responsibleUnitId = this.user.UnitId;
            }

            let handledByUser = finishOcc == 1 ? null : {
                id: this.user.Id,
                name: this.user.FullName,
                email: "",
                unitId: this.user.UnitId
            }


            let canceledByUser = finishOcc == 1 ? {
                id: this.user.Id,
                name: this.user.FullName,
                email: "",
                receiveAutonomousWarning: false,
                system: this.user.onlineType[0],
                unitId: this.user.UnitId
            } : null

            occurrenceToSave.reasonContent = JSON.stringify({ 
                reasons: this.reasonList, 
                occurrenceHandleBy: handledByUser,
                userThatCancel: canceledByUser
            })
            occurrenceToSave.content = JSON.stringify({ ...this.occurrence.content })

            occurrenceToSave.productionUnitId = parseInt(this.user.UnitId);
            occurrenceToSave.system = this.user.onlineType[0];

            occurrenceToSave.indicatorId = [Number(occurrenceToSave.indicatorId)]

            this.finishOrCancelUserMessage = '';
            const url = `${urlMainAPI}/saveoccurrence`;
            await axios
                .post(url, occurrenceToSave)
                .then((res) => {

                    defaultSuccess(res.data);
                    this.$bvModal.hide("UntratedOccurrences");
                })
                .catch((er) => {
                    showError(er.data);
                });

            this.loading = false;
            if (this.reloadOccFunct) {
                await this.reloadOccFunct()
            }
        },
        transformInfluenceTreeIndicator(influenceObj) {
            return {
                id: influenceObj.Id,
                productionUnitId: 1, // ou obtenha a partir do contexto, se necessário
                system: "P",
                indicatorName: `${influenceObj.Name} (${influenceObj.Unit})`, // concatena o nome com a unidade
                indicatorRule: "root", // valor padrão; ajuste se necessário
                timestamp: this.formatTimestamp(influenceObj.LastUpdated),
                onlineValue: influenceObj.CurrentValue.toString().replace('.', ','), // converte para string com vírgula
                onlineTarget: influenceObj.LastCropReal,
                lowerLimit: influenceObj.LowerLimit === -9999 ? "" : influenceObj.LowerLimit.toString(),
                upperLimit: influenceObj.UpperLimit === -9999 ? "" : influenceObj.UpperLimit.toString(),
                alert: influenceObj.OutOfRange,
                workshiftAIndicatorReability: "0", // valores fixos ou mapeados conforme sua lógica
                workshiftBIndicatorReability: "0",
                workshiftCIndicatorReability: "6,25",
                onlineRow: 1,
                onlineColumn: 1,
                occurrences: influenceObj.Occurrences,
                i: influenceObj.Id.toString(), // ajuste conforme necessário
                x: 0,
                y: 0,
                w: 1,
                h: 1,
                cardMinWidth: 1,
                cardMinHeight: 1,
                cardStatic: false,
                onlineGroup: "TELAGERAL",
                area: "Desempenho",
                moved: false
            }
        },
        formatTimestamp(timestamp) {
            const date = new Date(timestamp);
            const year = date.getFullYear();
            const month = ('0' + (date.getMonth() + 1)).slice(-2);
            const day = ('0' + date.getDate()).slice(-2);
            const hours = ('0' + date.getHours()).slice(-2);
            const minutes = ('0' + date.getMinutes()).slice(-2);
            const seconds = ('0' + date.getSeconds()).slice(-2);
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
    },

    async created() {
        this.loading = true;
        await this.loadResponsibles(this.user.UnitId);
        await this.getSystemConfigurations();

        this.chartOptions = {
            meanChart: meanChart,
            ppkChart: ppkChart,
            reabilityChart: reabilityChart,
            trendChart: trendChart
        };

        this.needDeadline = this.user && this.user.Permissions && this.user.Permissions.criaracaocomprazo && this.user.Permissions.criaracaocomprazo.allowed == true;

        this.chartOptions.trendChart.xaxis.min = this.trendStartDate
        this.chartOptions.trendChart.xaxis.min = this.trendEndDate

        // Se o objeto vindo do influence tree tiver a propriedade "Id", aplique a transformação
        if (this.indicatorInfo && this.indicatorInfo.Id) {
            this.indicator = this.transformInfluenceTreeIndicator(this.indicatorInfo);
        } else {
            this.indicator = this.indicatorInfo;
        }
        this.occurrence = this.occurrenceInfo;
        this.otherOccurrences = this.otherOccurrencesInfo != undefined ? this.otherOccurrencesInfo : [];


        if (this.indicator == null && this.occurrence.id) {
            await this.getIndicatorById(this.occurrence.indicatorId)
        }

        if (this.indicator != undefined && this.indicator.id && this.otherOccurrences != undefined && this.otherOccurrences.length > 0) {
            this.otherOccurrences = this.otherOccurrences.filter(item => item.indicatorId && item.indicatorId.toString() == this.indicator.id.toString() && item.id.toString() != this.occurrence.id.toString());
        }

        await this.getFaultTreeByIndicatorId(this.indicator.id, this.user);

        if (this.user.Permissions.enviarocorrenciasparasolvace != undefined) {
            if (this.user.Permissions.enviarocorrenciasparasolvace.allowed == true) {
                this.showSolvace = true
            }
        }

        await this.buildOccurrenceScreen();

        this.checkUserPermissions();

        this.checkFinishStatus++;

        this.checkCanFinish();

        if (this.occurrence.statusId != 1) {
            if (typeof this.occurrence.reasonContent == "string") this.occurrence.reasonContent = JSON.parse(this.occurrence.reasonContent);
            if (this.occurrence.reasonContent != null && this.reasonContent != '') {
                if (this.occurrence.reasonContent.occurrenceHandleBy != undefined && this.occurrence.reasonContent.occurrenceHandleBy.name != '') {
                    this.handledByName = this.occurrence.reasonContent.occurrenceHandleBy.name;
                }
            }
        }

        this.loading = false;
    }
}
</script>

<style scoped>
.occContainer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    height: 100%;
    column-gap: 0.8rem;
}

.graphContainer {
    width: 100%;
    display: grid;
    grid-template-columns: 1.05fr 1fr 1fr 5.05fr;
}

.infosContainer {
    display: grid;
    grid-template-columns: 2fr repeat(7, 1.2fr);
    grid-template-rows: 1fr, 1fr;
    padding: 0 0.2rem;
    column-gap: 0.6rem;
    row-gap: 0.3rem;
    width: 100%;
}

.occInfoComponent {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    column-gap: 0.1rem;
    font-size: 0.85rem;
}

.occInfoComponent p {
    margin: 0;
    padding: 0;
    font-weight: bold;
}

.reasonTitle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.4rem 0;
}

.reasonTitle p {
    font-weight: bold;
    margin: 0;
    font-size: 1.1rem;
}

.reasonTitle button,
article button {
    background-color: #ff5f00;
    color: white;
    font-weight: bold;
    border: 1px solid #ff5f00;
    border-radius: 0.2rem;
    font-size: 1rem;

    display: flex;
    align-items: center;
    column-gap: 0.4rem;
}

article {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    border: 1px solid gray;
    padding: 0.1rem;
}

article button {
    align-self: center;
    justify-self: center;
}

.reasonTitle button svg,
article button svg {
    color: #006F6F;
    stroke: white;
}

.reasonActionContainer {
    display: flex;
    flex-direction: column;
    width: 100%;
    row-gap: 0.5rem;
    overflow-y: auto;
    max-height: 10rem;
}

summary {
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.2rem 0.4rem;
    font-weight: bold;
    background-color: black;
    color: white;
    position: relative;
    column-gap: 1rem;
}

summary::after {
    content: "Visualizar Ações \02C3";
    transition: 0.2s;
    font-size: 0.9rem;
    background-color: #ff5f00;
    color: #fff;
    border-radius: 0.2rem;
    padding: 0.1rem 0.6rem;
}

details[open] summary::after {
    content: "Visualizar Ações \02C5";
    transition: 0.2s;
    font-size: 0.9rem;
    background-color: #ff5f00;
    color: #fff;
    border-radius: 0.2rem;
    padding: 0.1rem 0.6rem;
}

summary span {
    max-width: 70%;
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: nowrap;
}

.removeReasonBtn {
    display: block;
    margin-left: auto;
    font-size: 0.9rem;
    font-weight: bold;
    background-color: #FF1F1F;
    border: none;
    border-radius: 0.2rem;
    color: white;
    cursor: pointer;
}

.removeReasonBtn span {
    margin-right: 0.2rem;
}

.addActionBtn {
    margin: 0.2rem 0;
}

.actionsContainer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
}

.actionsContainer section {
    width: 100%;
}

.actionItem {
    width: 100%;
    border-bottom: 1px solid gray;

    display: grid;
    grid-template-columns: 3fr 2fr 1fr 1fr;
    align-items: center;
    padding: 0.1rem 0.2rem;
    column-gap: 0.5rem;
}

.actionItem strong {
    font-weight: bold;
    color: #ff5f00;
    margin-right: 0.2rem;
    font-size: 0.9;
}

.actionItem span {
    font-weight: normal;
    font-size: 0.9rem;
    color: rgb(59, 59, 59);
}

.actionItem .actionTitle {
    max-width: 95%;
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: nowrap;
}

.voidActions {
    margin: 0;
    padding: 0 0.2rem;
    text-align: left;
    width: 100%;
    border-bottom: 1px solid gray;
}

.actionBtn {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: 1.4rem;
}

.actionBtn svg {
    font-size: 1.15rem;
    fill: #ff5f00;
    cursor: pointer;
}

.workerOrderComponent {
    display: flex !important;
    align-items: center;
    column-gap: 0.1rem;
}

.workerOrderComponent svg {
    color: #ff5f00;
    font-size: 1rem;
    cursor: pointer;
}

.obsContainer {
    display: flex;
    flex-direction: row;
    /* flex-wrap: wrap; */

    align-items: center;
    justify-content: space-between;
    width: 100%;
    column-gap: 0.5rem;
    padding-top: 0.5rem;
}

.obsContainer div {
    width: 100%;
    display: flex;
    flex-direction: column;
}

.obsContainer textarea {
    width: 100%;
}

.obsContainer div span {
    font-weight: bold;
}

table th {
    position: -webkit-sticky;
    position: sticky;
    top: -1px;
    z-index: 1;
    background: #fff;
}

table td:hover {
    cursor: pointer;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}

table tr.occurrence-selected {
    background-color: rgba(65, 67, 69, 0.40) !important;
    color: white;
}

.div-table {
    min-height: 40px;
    max-height: 206px;
    overflow-y: auto;
    border-radius: 5px;
}

#finishOccModal textarea,
#cancelOccModal textarea {
    width: 100%;
}

.handledbycontainer {
    display: flex;
    column-gap: 0.5rem;
    margin-top: 0.5rem;
}
</style>